// Passo 2 · Requisiti (tela, artboard 32): il triage con PM e Team leader oppure «Ho già tutto»
// (si incollano piano o requisiti). In tutti e due i casi i requisiti possono andare su memo.
import { Field, TextArea, TextField } from '../../../components/Field';
import { Segmented } from '../../../components/Segmented';
import { Select } from '../../../components/Select';
import { Switch } from '../../../components/Switch';
import type { AgentProfile, ConnectedProvider, PlanMemo, TriageTurn } from '../../../lib/api';
import { Triage, type TriageState } from './Triage';

export type ReqMode = 'triage' | 'ready';

const SKELETON =
  '## Obiettivo\n\n## Utenti\n\n## Interfaccia e design\n\n## Funzioni\n\n## Vincoli\n\n## Fuori ambito\n\n## Criteri di accettazione\n';

function MemoPanel({
  memo,
  onMemo,
  links,
}: {
  memo: PlanMemo;
  onMemo: (m: PlanMemo) => void;
  links: { id: string; name: string }[];
}) {
  return (
    <section className="np-card">
      <Switch
        label="Aggiorna su memo"
        hint={
          links.length
            ? 'Al salvataggio i requisiti diventano una spec nel progetto memo; le volte dopo si aggiorna la stessa.'
            : 'Nessuna connessione memo: aggiungila in Strumenti.'
        }
        checked={memo.enabled}
        disabled={links.length === 0}
        onChange={(enabled) => onMemo({ ...memo, enabled })}
      />
      {memo.enabled && (
        <div className="np-two">
          {links.length > 1 && (
            <Field label="Connessione">
              <Select
                label="Connessione memo"
                value={memo.connection ?? links[0].id}
                onChange={(v) => onMemo({ ...memo, connection: v })}
                options={links.map((l) => ({ value: l.id, label: l.name }))}
                width="100%"
              />
            </Field>
          )}
          <Field label="Space">
            <TextField value={memo.space} onChange={(space) => onMemo({ ...memo, space })} />
          </Field>
          <Field label="Progetto memo">
            <TextField value={memo.project} onChange={(project) => onMemo({ ...memo, project })} />
          </Field>
        </div>
      )}
    </section>
  );
}

export function StepRequirements({
  mode,
  onMode,
  value,
  onChange,
  triage,
  onTriage,
  docs,
  onDocs,
  profiles,
  providers,
  triageRoles,
  runTriage,
  memo,
  onMemo,
  memoLinks,
}: {
  mode: ReqMode;
  onMode: (m: ReqMode) => void;
  value: string;
  onChange: (v: string) => void;
  triage: TriageState;
  onTriage: (s: TriageState) => void;
  docs: string;
  onDocs: (v: string) => void;
  profiles: AgentProfile[];
  providers: ConnectedProvider[];
  triageRoles: { lead: string; technical: string };
  runTriage: (reply: string | null) => Promise<TriageTurn>;
  memo: PlanMemo;
  onMemo: (m: PlanMemo) => void;
  memoLinks: { id: string; name: string }[];
}) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Segmented
        label="Come nascono i requisiti"
        value={mode}
        items={[
          { value: 'triage', label: 'Triage con PM e Team leader' },
          { value: 'ready', label: 'Ho già tutto' },
        ]}
        onChange={onMode}
      />
      {mode === 'triage' ? (
        <Triage
          state={triage}
          onState={onTriage}
          requirements={value}
          onRequirements={onChange}
          docs={docs}
          onDocs={onDocs}
          profiles={profiles}
          providers={providers}
          roles={triageRoles}
          run={runTriage}
        />
      ) : (
        <section className="np-card">
          <Field
            label="Piano o requisiti"
            hint="In Markdown. PM e Team leader li leggono per preparare team e lavori."
          >
            <TextArea value={value} onChange={onChange} rows={14} mono />
          </Field>
          {!value && (
            <button type="button" className="dash-more" onClick={() => onChange(SKELETON)}>
              Parti dallo schema dei requisiti
            </button>
          )}
        </section>
      )}
      <MemoPanel memo={memo} onMemo={onMemo} links={memoLinks} />
    </div>
  );
}
